<!-- Header -->
<div class="d-flex flex-wrap flex-stack mb-6">
  <div>
    <h3 class="fw-bold my-2">{{ 'orders.title' | translate }}</h3>
    <p class="text-muted fs-6 mb-0">{{ 'orders.subtitle' | translate }}</p>
  </div>
</div>

<!-- Filter Tabs -->
<div class="filter-tabs mb-6">
  <button class="filter-tab" [class.active]="activeFilter === 'all'" (click)="activeFilter = 'all'">
    <span>{{ 'orders.allOrders' | translate }}</span>
    <span class="badge badge-light-secondary ms-2">{{ allOrders.length }}</span>
  </button>
  <button class="filter-tab" [class.active]="activeFilter === 'pending'" (click)="activeFilter = 'pending'">
    <i class="fa fa-clock text-warning me-2"></i>
    <span>{{ 'orders.pending' | translate }}</span>
    @if (pendingOrders.length > 0) {
      <span class="badge badge-light-warning ms-2">{{ pendingOrders.length }}</span>
    }
  </button>
  <button class="filter-tab" [class.active]="activeFilter === 'paid'" (click)="activeFilter = 'paid'">
    <i class="fa fa-check-circle text-success me-2"></i>
    <span>{{ 'orders.paid' | translate }}</span>
    @if (paidOrders.length > 0) {
      <span class="badge badge-light-success ms-2">{{ paidOrders.length }}</span>
    }
  </button>
  <button class="filter-tab" [class.active]="activeFilter === 'cancelled'" (click)="activeFilter = 'cancelled'">
    <i class="fa fa-times-circle text-danger me-2"></i>
    <span>{{ 'orders.cancelled' | translate }}</span>
    @if (cancelledOrders.length > 0) {
      <span class="badge badge-light-danger ms-2">{{ cancelledOrders.length }}</span>
    }
  </button>
</div>

<!-- Loading State -->
@if (loading) {
  <div class="card mb-10">
    <div class="card-body text-center py-15">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'orders.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'orders.loading' | translate }}</p>
    </div>
  </div>
}

<!-- Empty State -->
@if (!loading && filteredOrders.length === 0) {
  <div class="card mb-10">
    <div class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-solid fa-receipt"></i>
      </div>
      @if (activeFilter === 'all') {
        <h4>{{ 'orders.noOrdersFound' | translate }}</h4>
        <p>{{ 'orders.noOrdersFoundHint' | translate }}</p>
      } @else if (activeFilter === 'pending') {
        <h4>{{ 'orders.noPendingOrders' | translate }}</h4>
        <p>{{ 'orders.noPendingOrdersHint' | translate }}</p>
      } @else if (activeFilter === 'paid') {
        <h4>{{ 'orders.noPaidOrders' | translate }}</h4>
        <p>{{ 'orders.noPaidOrdersHint' | translate }}</p>
      } @else {
        <h4>{{ 'orders.noCancelledOrders' | translate }}</h4>
        <p>{{ 'orders.noCancelledOrdersHint' | translate }}</p>
      }
    </div>
  </div>
}

<!-- Orders List -->
@if (!loading && filteredOrders.length > 0) {
  <div class="card mb-10">
    <div class="card-body p-0">
      @for (order of filteredOrders; track order.order_id) {
        <!-- Order Header Row -->
        <div class="order-row" [class.expanded]="expandedOrderId === order.order_id">
          <div class="d-flex flex-wrap align-items-center p-5">
            <!-- Order Icon & Info -->
            <div class="d-flex align-items-center flex-grow-1 me-5 cursor-pointer" (click)="toggleOrderDetails(order)">
              <div class="symbol symbol-50px me-4">
                <span class="symbol-label" [ngClass]="{
                  'bg-light-warning': order.status === 'pending',
                  'bg-light-success': order.status === 'paid',
                  'bg-light-danger': order.status === 'cancelled'
                }">
                  @if (order.status === 'pending') {
                    <i class="fa fa-clock text-warning fs-2"></i>
                  } @else if (order.status === 'paid') {
                    <i class="fa fa-check-circle text-success fs-2"></i>
                  } @else {
                    <i class="fa fa-times-circle text-danger fs-2"></i>
                  }
                </span>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2">
                  <span class="text-gray-800 fw-bold fs-5">
                    {{ order.event?.event_name || ('orders.eventRegistration' | translate) }}
                  </span>
                  <span class="badge" [ngClass]="getStatusBadgeClass(order.status)">
                    {{ getStatusTranslation(order.status) }}
                  </span>
                </div>
                <span class="text-muted fw-semibold d-block fs-7">
                  {{ 'orders.order' | translate }} #{{ order.order_id }} • {{ formatDate(order.created_at) }}
                  <i class="fa fa-chevron-down ms-2 fs-9 transition-transform"
                     [class.rotate-180]="expandedOrderId === order.order_id"></i>
                </span>
              </div>
            </div>

            <!-- Amount -->
            <div class="me-5">
              <span class="text-gray-800 fw-bold fs-4">
                {{ formatCurrency(order.total_cents || 0, order.currency) }}
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex gap-2">
              @if (order.status === 'pending') {
                <app-button
                  [variant]="'primary'"
                  [label]="'orders.payNow' | translate"
                  [iconClass]="'fab fa-stripe'"
                  [loading]="payingOrderId === order.order_id"
                  [disabled]="payingOrderId !== null || cancellingOrderId !== null"
                  (clicked)="payOrder(order)"
                />
                <app-button
                  [variant]="'danger'"
                  [label]="'orders.cancel' | translate"
                  [iconClass]="'fa fa-times'"
                  [loading]="cancellingOrderId === order.order_id"
                  [disabled]="payingOrderId !== null || cancellingOrderId !== null"
                  (clicked)="cancelOrder(order)"
                />
              }
              @if (order.status === 'paid') {
                <app-button
                  [variant]="'outline'"
                  [label]="'orders.receipt' | translate"
                  [iconClass]="'fa fa-download'"
                  (clicked)="downloadReceipt(order)"
                />
              }
            </div>
          </div>

          <!-- Expanded Order Details -->
          @if (expandedOrderId === order.order_id) {
            <div class="order-details p-5 border-top">
              <div class="row">
                <!-- Order Info -->
                <div class="col-md-4 mb-4">
                  <h6 class="fw-bold mb-3">
                    <i class="fa fa-info-circle text-primary me-2"></i>
                    {{ 'orders.orderInformation' | translate }}
                  </h6>
                  <div class="info-list">
                    <div class="info-item">
                      <span class="info-label">{{ 'orders.orderId' | translate }}</span>
                      <span class="info-value">#{{ order.order_id }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">{{ 'orders.status' | translate }}</span>
                      <span class="badge" [ngClass]="getStatusBadgeClass(order.status)">{{ getStatusTranslation(order.status) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">{{ 'orders.created' | translate }}</span>
                      <span class="info-value">{{ formatDate(order.created_at) }}</span>
                    </div>
                    @if (order.paid_at) {
                      <div class="info-item">
                        <span class="info-label">{{ 'orders.paidDate' | translate }}</span>
                        <span class="info-value">{{ formatDate(order.paid_at) }}</span>
                      </div>
                    }
                  </div>
                </div>

                <!-- Event Info -->
                <div class="col-md-4 mb-4">
                  <h6 class="fw-bold mb-3">
                    <i class="fa fa-calendar text-primary me-2"></i>
                    {{ 'orders.eventDetails' | translate }}
                  </h6>
                  @if (order.event) {
                    <div class="info-list">
                      <div class="info-item">
                        <span class="info-label">{{ 'orders.event' | translate }}</span>
                        <a [routerLink]="'/event-details/' + order.event.event_id" class="info-value text-primary">
                          {{ order.event.event_name }}
                        </a>
                      </div>
                      @if (order.event.start_date) {
                        <div class="info-item">
                          <span class="info-label">{{ 'orders.date' | translate }}</span>
                          <span class="info-value">{{ formatDate(order.event.start_date) }}</span>
                        </div>
                      }
                      @if (order.event.city) {
                        <div class="info-item">
                          <span class="info-label">{{ 'orders.location' | translate }}</span>
                          <span class="info-value">{{ order.event.city }}, {{ order.event.country }}</span>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="text-muted">{{ 'orders.eventDetailsNotAvailable' | translate }}</p>
                  }
                </div>

                <!-- Order Lines -->
                <div class="col-md-4 mb-4">
                  <h6 class="fw-bold mb-3">
                    <i class="fa fa-list text-primary me-2"></i>
                    {{ 'orders.items' | translate }}
                  </h6>
                  <div class="order-lines">
                    @for (line of order.order_lines; track line.order_line_id) {
                      <div class="order-line-item">
                        <div class="flex-grow-1">
                          <span class="d-block">{{ line.description }}</span>
                          <span class="text-muted fs-8">
                            @if (line.quantity > 1) {
                              {{ 'orders.qty' | translate }}: {{ line.quantity }} •
                            }
                            MVA {{ line.vat_rate || 0 }}%: {{ formatCurrency(line.vat_cents || 0, order.currency) }}
                          </span>
                        </div>
                        <span class="fw-semibold">{{ formatCurrency(line.total_cents, order.currency) }}</span>
                      </div>
                    }
                    <div class="order-line-item text-muted">
                      <span>{{ 'orders.subtotalExVat' | translate }}</span>
                      <span>{{ formatCurrency(getSubtotalExVat(order), order.currency) }}</span>
                    </div>
                    <div class="order-line-item text-muted">
                      <span>{{ 'orders.totalVat' | translate }}</span>
                      <span>{{ formatCurrency(order.total_vat_cents || 0, order.currency) }}</span>
                    </div>
                    <div class="order-total">
                      <span class="fw-bold">{{ 'orders.total' | translate }}</span>
                      <span class="fw-bold fs-5">{{ formatCurrency(order.total_cents || 0, order.currency) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
}
